/**
 * Share a clip (spec 06 card 5): from a transcript selection, a link that starts and stops at those
 * seconds (`?t=1:26:40-1:27:10`, played by vimeoClock.startAt), shown as the card it unfurls into
 * (frame, clip length, quote, speaker · meeting, date) with "Copy link" and "Copy quote with citation".
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Copy, Link2 } from "lucide-react";
import { meetingStore } from "../../stores/meetingStore";
import { clock } from "../../lib/format";
import type { Draft } from "../../stores/notesStore";
import type { Person, TranscriptSegment } from "../../types";
import { shareUrl } from "./ShareMeeting";
import { site } from "../../app/site";
import { track } from "../../lib/analytics";

export interface Clip { start: number; end: number; quote: string; speaker: Person | null }

/** The selected words' seconds (word timings when known, else the whole segment). */
export function clipFromDraft(seg: TranscriptSegment, d: Draft, people: Record<string, Person>): Clip {
  let start = seg.start, end = seg.end;
  if (seg.words?.length) {
    let pos = 0, a = -1, b = -1;
    seg.words.forEach((w, i) => {
      const from = pos, to = pos + w.w.length;
      if (a < 0 && to > d.startChar) a = i;
      if (from < d.endChar) b = i;
      pos = to + 1;
    });
    if (a >= 0 && b >= a) { start = seg.words[a].start; end = seg.words[b].end; }
  }
  return { start: Math.floor(start), end: Math.max(Math.ceil(end), Math.floor(start) + 1), quote: d.quote, speaker: seg.speakerId ? people[seg.speakerId] ?? null : null };
}

const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { timeZone: site.timeZone, month: "short", day: "numeric" });
const speakerName = (p: Person | null) => (p ? `${p.title ? `${p.title} ` : ""}${p.name}` : null);

export function ShareClipCard({ clip, top, onClose }: { clip: Clip; top: number; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const s = meetingStore.getState();
  const meeting = s.meeting!;
  // The public page's address, from the dashboard too (ShareMeeting.tsx shareUrl): whoever opens it can play the clip.
  const t = `${clock(clip.start)}-${clock(clip.end)}`;
  const link = shareUrl(meeting.id, t);
  const shown = `…${new URL(link).pathname}?t=${t}`;
  const frames = s.frames;
  const frame = [...frames].reverse().find((f) => f.t <= clip.start)?.url ?? frames[0]?.url ?? meeting.video?.thumbnailUrl;
  const who = speakerName(clip.speaker);
  const meta = [who, `${meeting.body}, ${day(meeting.startsAt)}`].filter(Boolean).join(" · ");
  const citation = `“${clip.quote}” — ${[who, meeting.body, `${day(meeting.startsAt)}, ${new Date(meeting.startsAt).getFullYear()}`, clock(clip.start)].filter(Boolean).join(", ")}. ${link}`;

  // Kept inside the transcript panel.
  useLayoutEffect(() => {
    const el = ref.current, panel = el?.offsetParent as HTMLElement | null;
    if (el && panel) el.style.top = `${Math.max(8, Math.min(top, panel.clientHeight - el.offsetHeight - 8))}px`;
  }, [top]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const onDown = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown, true);
    ref.current?.querySelector<HTMLButtonElement>(".share-copy")?.focus();
    return () => { removeEventListener("keydown", onKey); document.removeEventListener("pointerdown", onDown, true); };
  }, [onClose]);

  const copy = (text: string, mark: boolean) => void navigator.clipboard?.writeText(text).then(() => {
    track("share", { method: mark ? "copy" : "copy_citation", content_type: "clip", item_id: meetingStore.getState().meeting?.id ?? "" });
    if (mark) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  });

  return (
    <div ref={ref} className="share-card" role="dialog" aria-label="Share clip" style={{ top }}>
      <div className="share-preview">
        <span className="share-thumb" style={frame ? { backgroundImage: `url(${frame})` } : undefined}>
          <span className="share-length">{clock(clip.end - clip.start)}</span>
        </span>
        <span className="share-text">
          <span className="share-quote">“{clip.quote}”</span>
          <span className="share-meta">{meta}</span>
        </span>
      </div>
      <div className="share-link-row">
        <span className="share-link"><Link2 size={13} /><span className="share-url">{shown}</span></span>
        <button className="btn btn--primary share-copy" onClick={() => copy(link, true)}>
          {copied ? <Check size={13} /> : <Copy size={13} />}Copy link
        </button>
      </div>
      <button className="share-cite" onClick={() => copy(citation, false)}>Copy quote with citation</button>
    </div>
  );
}
